import React, {useCallback, useEffect, useRef, useState} from "react"

export function Scrubber({ show = true, title, interval, intervals, onChange }) {
  const divRef = useRef(null)
  const scrubberRef = useRef(null)

  const [isMouseDown, setIsMouseDown] = useState(() => false)
  const [leftOffset, setLeftOffset] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (divRef.current == null || !isLoaded) {
      return
    }

    const element = divRef.current.children[interval]
    const width = element.offsetWidth
    const totalElements = divRef.current.children.length
    
    let offset
    
    if (interval === 0) {
      // First element: position at the start
      offset = element.offsetLeft - (scrubberRef.current.offsetWidth / 4)
    } else if (interval === totalElements - 1) {
      // Last element: position at the end
      offset = (element.offsetLeft + width) - (scrubberRef.current.offsetWidth / 4)
    } else {
      // Middle elements: position at center
      offset = (element.offsetLeft + (width / 2)) - (scrubberRef.current.offsetWidth / 4)
    }
    
    if (leftOffset !== offset) {
      setLeftOffset(() => offset)
    }
  }, [interval, leftOffset, isLoaded])

  useEffect(() => {
    if (!show) {
      return
    }

    let timer = null
    const load = () => {
      if (!divRef.current || !scrubberRef.current || interval == null || leftOffset != null || isLoaded) {
        return
      }

      setIsLoaded(() => true)
      clearTimeout(timer)
    }

    timer = setInterval(load, 50)
    return () => clearInterval(timer)
  }, [interval, isLoaded, leftOffset, show])

  const handleOnMouseLeave = useCallback(() => {
    if (isMouseDown) {
      setIsMouseDown(() => false)
    }
  }, [isMouseDown])

  const handleOnMouseDown = useCallback(() => {
    if (isMouseDown) {
      return
    }

    setIsMouseDown(() => true)
  }, [isMouseDown])

  const handleOnMouseUp = useCallback(() => {
    if (!isMouseDown) {
      return
    }

    setIsMouseDown(false)
  }, [isMouseDown])

  const handleOnHover = useCallback((newInterval) => {
    if (!show || newInterval === interval) {
      return
    }

    onChange && onChange(newInterval)
  }, [interval, onChange, show])

  const scrubberStyle = {}
  if (leftOffset != null) {
    scrubberStyle.left = leftOffset
  } else {
    scrubberStyle.display = "none"
  }

  if (!show) {
    return null
  }

  return (
    <div className="scrubber-selector" onMouseLeave={handleOnMouseLeave} onMouseDown={handleOnMouseDown} onMouseUp={handleOnMouseUp}>
      <span className="da-modal-entry-input-label">{title}</span>
      <div className="scrubber-selector__content">
        <div ref={divRef}>
          {intervals.map((value, i) => <ScrubberItem key={`scrubber-interval-${i}`} interval={value} isMouseDown={isMouseDown} onHover={handleOnHover} />)}
        </div>
        <p ref={scrubberRef} style={scrubberStyle} />
      </div>
    </div>
  )
}

function ScrubberItem({ interval, isMouseDown, onHover }) {
  const [isHovering, setIsHovering] = useState(() => false)

  const handleOnMouseEnter = useCallback(() => {
    setIsHovering(() => true)
  }, [])

  const handleOnMouseLeave = useCallback(() => {
    setIsHovering(() => false)
  }, [])

  useEffect(() => {
    if (isHovering && isMouseDown && onHover) {
      onHover(interval)
    }
  }, [interval, isHovering, isMouseDown, onHover])

  return (
    <div className="scrubber-selector-item" onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave} />
  )
}
